import * as React from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { debounce } from "@mui/material";
import { User, UserCheck, UserX } from "lucide-react";

import { axiosInstance } from "./../../utility/apiInstance";
import { useRole } from "../../components/MangeContextProvide";
import SearchInput from "../../common/SearchInput";
import StatCard from "../../common/StatCard";

const PAGE_SIZE = 10;

const moduleOptions: Record<string, string> = {
  "1": "Air",
  "2": "Ocean",
};

export default function CustomerManage() {
  const navigate = useNavigate();
  const { role } = useRole() as any;

  const isCustomerAdmin = role === "customeradmin";

  // role options mirror the old singleSelect valueOptions
  const roleOptions =
    role === "admin"
      ? [
          { value: "customeradmin", label: "customeradmin" },
          { value: "groundstaff", label: "groundstaff" },
        ]
      : [
          { value: "customeruser", label: "CustomerUser" },
          { value: "groundstaff", label: "GroundStaff" },
        ];

  const [rows, setRows] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [currentPage, setCurrentPage] = React.useState(0);

  const [totalCustomersAdmin, setTotalCustomersAdmin] = React.useState(0);
  const [activeCustomersAdmin, setActiveCustomersAdmin] = React.useState(0);
  const [inActiveCustomersAdmin, setInActiveCustomersAdmin] = React.useState(0);

  const [searchQuery, setSearchQuery] = React.useState("");
  const [debouncedSearch, setDebouncedSearch] = React.useState("");

  /* ── data ────────────────────────────────────────────────────────────── */

  const handleLogout = () => {
    localStorage.clear();
    navigate("/");
  };

  const getCustomerList = async (search: string = "") => {
    setLoading(true);
    try {
      const response = await axiosInstance.get(
        `api/v1/customers-auth/all-customers?search=${search}`
      );
      const fetchedRows = response?.data?.data || [];
      const data = fetchedRows.map((row: any) => ({
        ...row,
        key: row?._id || row?.id,
        id: row?._id || row?.id,
        name: row?.name,
        number: row?.contactInfo?.phone,
        email: row?.contactInfo?.email,
        organisation: row?.organisationId,
        module: row?.module,
        role: row?.role,
        globalData: row?.globalData,
        globalDirectory: row?.globalDirectory,
      }));
      setRows(data);
      if (response.data.statusCode === 401) handleLogout();
    } catch (err: any) {
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomerCount = async () => {
    try {
      const response = await axiosInstance.get(`/api/v1/organisation/customer-count`);
      if (response?.data?.success) {
        const { totalCustomers, activeCustomers, inActiveCustomers } = response.data.data;
        setTotalCustomersAdmin(totalCustomers);
        setActiveCustomersAdmin(activeCustomers);
        setInActiveCustomersAdmin(inActiveCustomers);
      }
    } catch (error: any) {}
  };

  // debounce search
  React.useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchQuery), 500);
    return () => clearTimeout(t);
  }, [searchQuery]);

  React.useEffect(() => {
    getCustomerList(debouncedSearch);
  }, [debouncedSearch]);

  React.useEffect(() => {
    setCurrentPage(0);
  }, [debouncedSearch]);

  React.useEffect(() => {
    role === "admin" && fetchCustomerCount();
  }, []);

  /* ── inline updates ──────────────────────────────────────────────────── */

  // Active / soft-delete toggle
  const debouncedHandleDeleteClick = debounce(async (id: any, row: any) => {
    const url = `/api/v1/customers-auth/customer/delete/${id}`;
    const payload = { isActive: !row.isActive };
    try {
      const response = await axiosInstance.delete(url, { data: payload });
      if (response.status !== 200) {
        toast.error("Error updating customer status.");
      } else {
        toast.success(response?.data?.message);
        getCustomerList("");
        fetchCustomerCount();
      }
    } catch (error: any) {
      error?.response?.data?.error
        ? toast.error(error.response.data.error)
        : toast.error("An error occurred while updating customer status.");
    }
  }, 300);

  // Global data toggle (optimistic + revert)
  const handleGlobalDataToggle = async (id: any, checked: boolean) => {
    const value = checked ? 1 : 0;
    setRows((prevRows: any) =>
      prevRows.map((row: any) => (row.id === id ? { ...row, globalData: value } : row))
    );
    try {
      const url = `/api/v1/customers-auth/customer/update/status/${id}`;
      const response = await axiosInstance.patch(url, { status: value });
      if (response?.data?.success) {
        toast.success("Status Updated");
      }
    } catch (error: any) {
      setRows((prevRows: any) =>
        prevRows.map((row: any) => (row.id === id ? { ...row, globalData: value ? 0 : 1 } : row))
      );
      toast.error("Unable Update Status");
    }
  };

  // Inline role change (optimistic + revert). Reuses the customer update endpoint.
  const handleRoleChange = async (row: any, newRole: string) => {
    const prevRole = row.role;

    // optimistic
    setRows((prevRows: any) =>
      prevRows.map((r: any) => (r.id === row.id ? { ...r, role: newRole } : r))
    );

    const sendData = {
      name: row.name,
      username: row.email,
      role: newRole,
      module: Array.isArray(row.module) ? row.module : [row.module],
      organisationId: row.organisation?._id,
      contactInfo: {
        email: row.email,
        phone: row.number,
      },
      globalData: row.globalData || 0,
      globalDirectory: true,
    };

    try {
      const url = `api/v1/customers-auth/customer/update/${row._id}`;
      const response = await axiosInstance.patch(url, sendData);
      toast.success(response.data?.message || "Role updated successfully");
      getCustomerList("");
    } catch (err: any) {
      // revert
      setRows((prevRows: any) =>
        prevRows.map((r: any) => (r.id === row.id ? { ...r, role: prevRole } : r))
      );
      if (Array.isArray(err?.response?.data?.error)) {
        err.response.data.error.forEach((e: any) => toast.error(`${e.message}`));
      } else {
        toast.error(err?.response?.data?.error || "An error occurred while updating role.");
      }
    }
  };

  const moduleLabel = (m: any) =>
    Array.isArray(m) ? m.map((v: string) => moduleOptions[v]).filter(Boolean).join(", ") : "";

  const pageCount = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const pagedRows = rows.slice(currentPage * PAGE_SIZE, currentPage * PAGE_SIZE + PAGE_SIZE);
  const colSpan = isCustomerAdmin ? 9 : 7;

  /* ── render ──────────────────────────────────────────────────────────── */

  return (
    <>
      {/* Stat cards (admin only) */}
      {role === "admin" && (
        <div
          style={{
            display: "flex",
            gap: 16,
            flexWrap: "wrap",
            marginTop: 8,
            marginBottom: 16,
          }}
        >
          <StatCard
            label="Users"
            value={totalCustomersAdmin}
            color="#f59e0b"
            icon={<User size={22} color="#f59e0b" />}
          />
          <StatCard
            label="Active users"
            value={activeCustomersAdmin}
            color="#0f9d58"
            icon={<UserCheck size={22} color="#0f9d58" />}
          />
          <StatCard
            label="Inactive users"
            value={inActiveCustomersAdmin}
            color="#d92d20"
            icon={<UserX size={22} color="#d92d20" />}
          />
        </div>
      )}

      {/* Card: toolbar + table */}
      <div className="ctpl-card">
        <div className="ctpl-toolbar">
          <SearchInput placeholder="Search" onChange={setSearchQuery} width={340} />
          <div className="ctpl-spacer" />
        </div>

        <div className="ctpl-table-scroll">
          <table className="ctpl-table" style={{ minWidth: 900 }}>
            <thead>
              <tr>
                <th style={{ width: 60 }}>S.No.</th>
                <th>Name</th>
                <th>Mobile</th>
                <th>Email ID</th>
                {isCustomerAdmin && <th>Module</th>}
                <th>Organisation</th>
                <th>Role</th>
                {isCustomerAdmin && <th>Global Data</th>}
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td className="ctpl-empty-cell" colSpan={colSpan}>
                    Loading users…
                  </td>
                </tr>
              ) : pagedRows.length === 0 ? (
                <tr>
                  <td className="ctpl-empty-cell" colSpan={colSpan}>
                    No users found
                  </td>
                </tr>
              ) : (
                pagedRows.map((row: any, index: number) => {
                  // make sure the current role is always selectable (customeradmin only)
                  const opts = !isCustomerAdmin
                    ? roleOptions
                    : roleOptions.some((o) => o.value === row.role)
                    ? roleOptions
                    : [{ value: row.role, label: row.role }, ...roleOptions];
                  return (
                    <tr key={row.id}>
                      <td>{currentPage * PAGE_SIZE + index + 1}</td>
                      <td>
                        <span className="ctpl-tmpl-name">{row.name}</span>
                      </td>
                      <td>{row.number}</td>
                      <td>{row.email}</td>
                      {isCustomerAdmin && <td>{moduleLabel(row.module)}</td>}
                      <td>{row?.organisation?.organizationName}</td>
                      <td>
                        {isCustomerAdmin ? (
                          <select
                            value={row.role}
                            onChange={(e) => {
                              const v = e.target.value;
                              if (v !== row.role) handleRoleChange(row, v);
                            }}
                            style={{
                              padding: "6px 8px",
                              border: "1px solid var(--ctpl-line)",
                              borderRadius: 6,
                              fontSize: 13,
                              background: "#fff",
                              color: "var(--ctpl-ink)",
                              cursor: "pointer",
                              minWidth: 130,
                            }}
                          >
                            {opts.map((o) => (
                              <option key={o.value} value={o.value}>
                                {o.label}
                              </option>
                            ))}
                          </select>
                        ) : (
                          row.role
                        )}
                      </td>
                      {isCustomerAdmin && (
                        <td>
                          <button
                            className={`ctpl-switch ${row.globalData ? "on" : ""}`}
                            disabled={row.role === "groundstaff"}
                            onClick={() =>
                              handleGlobalDataToggle(row.id, !Boolean(row.globalData))
                            }
                            aria-label="Toggle global data"
                            style={
                              row.role === "groundstaff"
                                ? { opacity: 0.5, cursor: "not-allowed" }
                                : undefined
                            }
                          />
                        </td>
                      )}
                      <td>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <button
                            className={`ctpl-switch ${row.isActive ? "on" : ""}`}
                            onClick={() => debouncedHandleDeleteClick(row.id, row)}
                            aria-label="Toggle active status"
                          />
                          <span
                            style={{
                              fontSize: 12,
                              fontWeight: 600,
                              color: row.isActive
                                ? "var(--ctpl-success)"
                                : "var(--ctpl-ink-soft)",
                            }}
                          >
                            {row.isActive ? "Active" : "Inactive"}
                          </span>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <div className="ctpl-table-footer">
          <span>
            {rows.length} user{rows.length === 1 ? "" : "s"}
          </span>
          <div className="ctpl-pagination">
            <button
              className="ctpl-page-btn"
              disabled={currentPage === 0}
              onClick={() => setCurrentPage((p) => Math.max(0, p - 1))}
            >
              ‹
            </button>
            <span>
              Page {currentPage + 1} of {pageCount}
            </span>
            <button
              className="ctpl-page-btn"
              disabled={currentPage >= pageCount - 1}
              onClick={() => setCurrentPage((p) => Math.min(pageCount - 1, p + 1))}
            >
              ›
            </button>
          </div>
        </div>
      </div>
    </>
  );
}